import ReactDOM from 'react-dom';
import { makeStyles } from '@mui/styles';
import { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItemButton,
  Typography,
  Grid,
  ClickAwayListener,
  CircularProgress,
  IconButton,
  Chip,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import { get } from 'lodash';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router';
import moment from 'moment';

import { MIconButton } from '../../../@material-extend';
import CONFIG from '@/config';
import { formDataInstance } from '@/common/axios';
import { calculateRelativeRoughAge } from '@/common/utils/calculateAge';
import { UpRightArrowIcon } from './UpRightArrowIcon';
import { PATH_APP } from '@/routes/paths';
import { Id2Gql } from '@/common/utils/utils';
import { NavStyles } from '.';
import { BellIcon } from '../../../common/icons/BellIcon';
import { getErrorDetails } from '@/views/Pages/Customer/CustomerProfile/CustomerTabs/NewAlertsNotes/ApiErrorLookup';
import { getStoreLocalTime } from '@/common/utils/time';
import ListSortFilter, {
  getSortOnFieldFn,
  ListFilterOption,
  ListSortOption
} from '@/views/Pages/Customer/CustomerProfile/CustomerTabs/NewAlertsNotes/ListSortFilter';
import AddEditNoteModal from '@/views/Pages/Customer/CustomerProfile/CustomerTabs/NewAlertsNotes/AddEditNoteModal';
import {
  NotificationNote,
  StaffNote
} from '@/views/Pages/Customer/CustomerProfile/CustomerTabs/NewAlertsNotes/Types';

const useStyles = makeStyles(theme => ({
  activityBadge: {
    color: 'white',
    fontSize: 12,
    backgroundColor: theme.palette.error[600],
    borderRadius: 12,
    padding: '0 3px 0 3px',
    fontWeight: 600,
    height: 16,
    lineHeight: '16px',
    zIndex: 101,
    '&.condensed': {
      position: 'absolute',
      top: 2,
      left: 18
    }
  },
  listContainer: {
    maxHeight: 'calc(80vh - 44px)',
    overflowY: 'auto',
    backgroundColor: 'white',
    paddingTop: 0,
    paddingBottom: 0
  },
  list: {
    position: 'absolute',
    width: 460,
    backgroundColor: 'white',
    color: 'black',
    boxShadow:
      '0px 0px 2px 0px rgb(145 158 171 / 24%), 0px 16px 32px -4px rgb(145 158 171 / 24%)',
    borderRadius: 4,
    top: 120,
    left: 50,
    zIndex: 1501,
    overflow: 'visible',
    '&.expanded': {
      left: 230
    }
  },
  tabButton: {
    padding: '14px 10px',
    marginBottom: 0,
    paddingBottom: 14,
    cursor: 'pointer'
  },
  tabButtonActive: {
    borderBottom: `4px solid ${theme.palette.primary[600]}`
  },
  listTabContainer: {
    marginTop: 0,
    padding: 0,
    borderBottom: '.5px solid rgba(145, 158, 171, 0.24)',
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    backgroundColor: 'white'
  },
  listItemRow1: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: theme.palette.text.primary[600],
    '& a': {
      textDecoration: 'none',
      fontSize: 14,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      maxWidth: 336
    }
  },
  listItemRow2: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row'
  },
  loading: {
    position: 'absolute',
    width: '100%',
    backgroundColor: '#fff',
    zIndex: 3,
    height: '100%',
    opacity: 0.8,
    borderRadius: 8,
    paddingTop: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      color: theme.palette.primary['restoreBlue']
    }
  },
  seeAllButton: {
    padding: '4px 16px',
    height: 60,
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: 'white',
    justifyContent: 'center',
    color: theme.palette.primary[600],
    borderRadius: 4,
    margin: 'auto'
  },
  listButton: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: '.5px solid rgba(145, 158, 171, 0.24)',
    paddingRight: 20,
    paddingTop: 10,
    cursor: 'default'
  },
  categoryText: {
    color: theme.palette.primary['restoreBlue'],
    fontWeight: 500
  },
  statusText: {
    color: theme.palette.text.secondary
  },
  assigneeText: {
    color: theme.palette.text.secondary,
    maxWidth: 174,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  subStatus: {
    fontSize: 12,
    fontWeight: 500
  },
  subStatusOverdue: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.error[400]
  },
  showMoreButton: {
    borderRadius: '7px',
    textTransform: 'capitalize'
  },
  showingAll: {
    margin: 'auto',
    color: theme.palette.grey[300],
    fontSize: 14
  },
  noItemsMsg: {
    margin: 'auto',
    color: theme.palette.grey[300],
    fontSize: 14,
    fontWeight: 400
  },
  sortContainer: {
    textAlign: 'right',
    marginLeft: 36,
    marginTop: 14,
    marginRight: 6,
    width: 240,
    '& div': {
      fontSize: 12
    }
  },
  sortByText: {
    textAlign: 'left',
    whiteSpace: 'nowrap',
    margin: 'auto',
    paddingRight: '12px',
    paddingBottom: '4px',
    color: theme.palette.text.secondary
  },
  sortSelect: {
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.primary[600],
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary[600]
    }
  },
  customerName: {
    maxWidth: 256,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: theme.palette.primary[600],
    cursor: 'pointer'
  },
  noteCategory: {
    maxWidth: 144,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: theme.palette.primary[600]
  },
  alertIcon: {
    color: `${theme.palette.primary[900]} !important`
  },
  leadAge: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.text.secondary
  },
  leadReferredBy: {
    color: theme.palette.text.secondary,
    '& button': {
      padding: '6px 12px',
      marginRight: 4,
      height: 40
    },
    '& .MuiTypography-caption': {
      maxWidth: 330,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      display: 'inline-block'
    }
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1
  },
  referrerValue: {
    fontSize: 12,
    lineHeight: 1,
    verticalAlign: 'middle',
    fontWeight: 300,
    maxWidth: 234,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'inline-block'
  },
  campaignValue: {
    fontSize: 12,
    lineHeight: 1,
    verticalAlign: 'middle',
    fontWeight: 300,
    maxWidth: 244,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'inline-block'
  },
  dismiss: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    '& button': {
      width: 80,
      height: 36,
      padding: '4px 8px',
      fontSize: 14,
      position: 'absolute',
      bottom: 12
    }
  },
  capaignLink: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1
  },
  arrowIcon: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1,
    verticalAlign: 'text-bottom'
  }
}));

const alertCountPollDelay = 1000 * 60 * 5; // 5 mins
const initialItemsToShow = 8;

let noteItemsRefreshTimer = null;
const noteItemRefreshDelay = 1000 * 20; // 20s

let noteCountRefreshTimer = null;
const noteCountRefreshDelay = 1000 * 20; // 20s

const TABS = {
  NOTES: 'NOTES',
  LEADS: 'LEADS'
};
interface NotificationListResponse {
  data: {
    data: NotificationNote[];
  };
}

export const addViewStates = (notes: NotificationNote[]) => {
  const now = getStoreLocalTime();
  return notes.map(note => {
    const dueToday =
      note?.followupDate && moment(now).isSame(note?.followupDate, 'day');
    const followupInPast =
      note?.followupDate && moment(now).isAfter(note?.followupDate);
    return {
      ...note,
      customer: {
        ...note.customer,
        fullName: `${note.customer.firstName} ${note.customer.lastName}`
      },
      subStatus: dueToday
        ? 'Due Today'
        : followupInPast
          ? 'Overdue'
          : note?.followupDate
    };
  });
};

const reshapeNoteForEdit = (note: NotificationNote): StaffNote => {
  if (note) {
    return {
      id: note?.id,
      categoryId: note?.noteCategory?.id,
      createdBy: note.createdBy,
      createdAt: note.createdAt,
      content: note.note,
      updatedAt: note.updatedAt,
      status: note.status,
      assignee: {
        id: note?.assignee?.id
      },
      contactedVia: note.contactedVia,
      followupTime: note.followupTime,
      followupDate: note.followupDate
    };
  } else {
    return null;
  }
};

export const sortSelections: ListSortOption[] = [
  {
    value: 1,
    display: 'Due Date (Sooner)',
    sortFn: getSortOnFieldFn('followupDate', 1)
  },
  {
    value: 2,
    display: 'Due Date (Later)',
    sortFn: getSortOnFieldFn('followupDate', -1)
  },
  {
    value: 3,
    display: 'Assignee (A-Z)',
    sortFn: getSortOnFieldFn('assignee.name', 1)
  },
  {
    value: 4,
    display: 'Assignee (Z-A)',
    sortFn: getSortOnFieldFn('assignee.name', -1)
  },
  {
    value: 5,
    display: 'Category (A-Z)',
    sortFn: getSortOnFieldFn('noteCategory.name', 1)
  },
  {
    value: 6,
    display: 'Category (Z-A)',
    sortFn: getSortOnFieldFn('noteCategory.name', -1)
  },
  {
    value: 7,
    display: 'Status (A-Z)',
    sortFn: getSortOnFieldFn('status', 1)
  },
  {
    value: 8,
    display: 'Status (Z-A)',
    sortFn: getSortOnFieldFn('status', -1)
  },
  {
    value: 9,
    display: 'Customer (A-Z)',
    sortFn: getSortOnFieldFn('customer.fullName', 1)
  },
  {
    value: 10,
    display: 'Customer (Z-A)',
    sortFn: getSortOnFieldFn('customer.fullName', -1)
  }
];

export const getNotesApiSuffix = '/staff/user-notes';

// Alerts namenclature refer to notes and leads
const Activity = props => {
  const navClasses = NavStyles();
  const { isOpen } = props;
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [notes, setNotes] = useState<NotificationNote[]>([]);
  const [notesListView, setNoteListView] = useState<NotificationNote[]>([]);

  const [noteItemRefresh, setNoteItemRefresh] = useState(true);

  const [leads, setLeads] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const [alertCountRefresh, setAlertCountRefresh] = useState(true);
  const [noteCount, setNoteCount] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const [visibleTab, setVisibleTab] = useState(TABS.LEADS);
  const [noteItemsToShow, setNoteItemsToShow] =
    useState<number>(initialItemsToShow);
  const [leadItemsToShow, setLeadItemsToShow] =
    useState<number>(initialItemsToShow);
  const [showAddEditNoteModal, setShowAddEditNoteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [filterSelection, setFilterSelection] =
    useState<ListFilterOption | null>(null);
  const [sortSelection, setSortSelection] = useState<ListSortOption>(
    sortSelections[0]
  );

  const getLeads = async () => {
    setLeadsLoading(true);
    await formDataInstance
      .get(CONFIG.USERS_API_URL + '/staff/leads')
      .then((response: any) => {
        const data = response?.data?.data;
        setLeads(
          data.map(l => ({
            ...l,
            leadAge: calculateRelativeRoughAge(l.createdAt),
            legacyId: Id2Gql('Customer', l.customerId)
          }))
        );
        setLeadCount(data.length);
      })
      .finally(() => setLeadsLoading(false));
  };

  const getNotes = async () => {
    setNotesLoading(true);
    formDataInstance
      .get(
        `${CONFIG.USERS_API_URL}${getNotesApiSuffix}?parentThredsOnly=true&ignoreStatusComplete=true&pageSize=400`
      )
      .then((response: NotificationListResponse) => {
        const notes = response.data.data;
        setNotes(addViewStates(notes));
        setNoteCount(notes.length);
      })
      .catch(error =>
        enqueueSnackbar(
          getErrorDetails(`There was a problem getting Notes.`, error),
          {
            variant: 'error',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          }
        )
      )
      .finally(() => setNotesLoading(false));
  };

  const getAlertCount = async () => {
    // for lead + note count for the store
    await formDataInstance
      .get(`${CONFIG.USERS_API_URL}/staff/notification-count`)
      .then(response => {
        setLeadCount(get(response, 'data.data.leadCount', '-'));
        setNoteCount(get(response, 'data.data.noteCount', '-'));
        setTotalCount(get(response, 'data.data.totalCount', '-'));
      });
  };

  useEffect(() => {
    getAlertCount();
    delayGetAlertCount();
  }, []);

  useEffect(() => {
    if (visibleTab === TABS.NOTES) {
      getNotes();
    } else {
      getLeads();
    }
  }, [visibleTab]);

  // if list, filter or sort change update the visible list
  useEffect(() => {
    let sortedFilteredList = [...notes];
    if (sortSelection !== null) {
      sortedFilteredList = sortedFilteredList.sort(sortSelection.sortFn);
    }
    setNoteListView(sortedFilteredList.slice(0, 400));
    setNoteCount(sortedFilteredList.length);
  }, [notes, sortSelection, filterSelection]);

  const showMoreNotes = () => setNoteItemsToShow(notes?.length);
  const showLessNotes = () => setNoteItemsToShow(8);
  const showMoreLeads = () => setLeadItemsToShow(leads?.length);
  const showLessLeads = () => setLeadItemsToShow(8);

  const handleEditNote = (n: NotificationNote) => {
    setSelectedNote(n);
    setShowAddEditNoteModal(true);
  };

  const delayGetAlertCount = () => {
    setInterval(() => {
      getAlertCount();
    }, alertCountPollDelay);
  };

  const resetAlertItemRefreshDelayed = () => {
    setNoteItemRefresh(false);
    clearTimeout(noteItemsRefreshTimer);
    noteItemsRefreshTimer = setTimeout(() => {
      setNoteItemRefresh(true);
    }, noteItemRefreshDelay);
  };

  const resetAlertCountRefreshDelayed = () => {
    setAlertCountRefresh(false);
    clearTimeout(noteCountRefreshTimer);
    noteCountRefreshTimer = setTimeout(() => {
      setAlertCountRefresh(true);
    }, noteCountRefreshDelay);
  };

  const userActionGetItems = () => {
    if (visibleTab === TABS.LEADS) {
      getLeads();
    } else if (noteItemRefresh) {
      // don't request everytime the user acts, only once every noteItemRefreshDelay (milliseconds)
      getNotes();
      resetAlertItemRefreshDelayed();
    }
  };

  const userActionGetNoteCount = () => {
    // don't request everytime the user acts, only once every noteItemRefreshDelay (milliseconds)
    if (alertCountRefresh) {
      getAlertCount();
      resetAlertCountRefreshDelayed();
    }
  };

  // material renders some dropdowns outside of the local area of dom
  // so we need additional logic to discover this and cancel the list closing
  // add additional cases here as needed:
  const clickAwayFilter = e => {
    const isWithin =
      e?.target?.children[e?.target?.children?.length - 1]?.id ===
      'menu-Sort By';
    const isSortFilterMenu =
      e?.target?.children[e?.target?.children?.length - 1]?.id === 'menu-';
    if (!isWithin && !isSortFilterMenu && !showAddEditNoteModal) {
      setListOpen(false);
      setNoteItemsToShow(initialItemsToShow);
    }
  };

  const toggleListOpen = () => {
    setListOpen(!listOpen);
    if (!listOpen) {
      userActionGetItems();
    } else {
      setNoteItemsToShow(initialItemsToShow);
    }
  };

  const dismissLead = async (id: number) => {
    setLeadsLoading(true);
    await formDataInstance
      .delete(`${CONFIG.USERS_API_URL}/staff/leads?customerId=${id}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        if (response.data.data.success) {
          setLeads([...leads.filter(l => l.customerId !== id)]);
          enqueueSnackbar(`Lead was dismissed successfully!`, {
            variant: 'success',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          });
        }
      })
      .catch(() => {
        enqueueSnackbar(`There was a problem dismissing lead!`, {
          variant: 'error',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)} />
          )
        });
      })
      .finally(() => setLeadsLoading(false));
  };

  const openReferral = (url: string) => {
    window.open(url, '_blank');
  };

  const loading = notesLoading || leadsLoading;

  const popout = (
    <ClickAwayListener onClickAway={clickAwayFilter}>
      <Box className={`${classes.list} ${isOpen ? 'expanded' : ''}`}>
        {loading && (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        )}
        <Grid container className={classes.listTabContainer}>
          <Box
            className={clsx(
              classes.tabButton,
              visibleTab === TABS.NOTES ? classes.tabButtonActive : ''
            )}
            onClick={() => setVisibleTab(TABS.NOTES)}
          >
            <Typography variant="body3">
              Notes&nbsp;
              {noteCount > 0 && `(${noteCount})`}
            </Typography>
          </Box>
          <Box
            className={clsx(
              classes.tabButton,
              visibleTab === TABS.LEADS ? classes.tabButtonActive : ''
            )}
            onClick={() => setVisibleTab(TABS.LEADS)}
          >
            <Typography variant="body3">
              Leads&nbsp;
              {leadCount > 0 && `(${leadCount})`}
            </Typography>
          </Box>
          <Box className={classes.sortContainer}>
            {visibleTab === TABS.NOTES && (
              <ListSortFilter
                setFilterSelection={setFilterSelection}
                setSortSelection={setSortSelection}
                filterSelections={[]}
                sortSelections={sortSelections}
              />
            )}
          </Box>
        </Grid>
        {visibleTab === TABS.NOTES && (
          <>
            <List className={classes.listContainer}>
              {notesListView
                ?.slice(0, noteItemsToShow)
                .map((n: NotificationNote, index) => {
                  const overdueClass =
                    n.subStatus === 'Overdue'
                      ? classes.subStatusOverdue
                      : classes.subStatus;
                  const fn = n?.assignee?.firstName;
                  const ln = n?.assignee?.lastName;
                  const assignee = `${fn ? fn : ''} ${ln ? ln : ''}`;
                  return (
                    <div title={`${n.note}`}>
                      <ListItemButton
                        key={index}
                        className={classes.listButton}
                        onClick={() => handleEditNote(n)}
                      >
                        <Grid container className={classes.listItemRow1}>
                          <Box className={classes.customerName}>
                            <Typography variant="body3">
                              {n.customer?.firstName
                                ? `${n.customer.firstName} ${n.customer.lastName}`
                                : ''}
                            </Typography>
                          </Box>
                          <Box className={classes.noteCategory}>
                            <Typography variant="caption">
                              {n?.noteCategory.name}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid container className={classes.listItemRow2}>
                          <Grid item xs={2} className={classes.statusText}>
                            <Typography variant="caption">
                              {n?.status}
                            </Typography>
                          </Grid>
                          <Grid item xs={5} className={classes.assigneeText}>
                            <Typography variant="caption">
                              {'Assignee: '}
                            </Typography>
                            <Typography variant="caption">
                              {assignee}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sx={{ textAlign: 'right' }}>
                            {<Box className={overdueClass}>{n?.subStatus}</Box>}
                          </Grid>
                        </Grid>
                      </ListItemButton>
                    </div>
                  );
                })}
            </List>
            <Grid className={classes.seeAllButton}>
              {noteCount === 0 && (
                <Box className={classes.noItemsMsg}>No notes to show.</Box>
              )}
              {notes?.length > noteItemsToShow && (
                <Box sx={{ margin: 'auto' }}>
                  {noteItemsToShow === 8 ? (
                    <IconButton size="small" onClick={showMoreNotes}>
                      <Chip
                        className={classes.showMoreButton}
                        clickable
                        color="primary"
                        variant="outlined"
                        label={`See All Notes`}
                      />
                    </IconButton>
                  ) : (
                    <IconButton size="small" onClick={showLessNotes}>
                      <Chip
                        className={classes.showMoreButton}
                        clickable
                        variant="outlined"
                        color="primary"
                        label={`See Less Notes`}
                      />
                    </IconButton>
                  )}
                </Box>
              )}
              {notes?.length === noteItemsToShow && (
                <Box className={classes.showingAll}>
                  Showing {noteItemsToShow} of {notes?.length} Notes
                </Box>
              )}
            </Grid>
          </>
        )}
        {visibleTab === TABS.LEADS && (
          <>
            <List className={classes.listContainer}>
              {leadCount > 0 &&
                leads?.slice(0, leadItemsToShow).map((l, index) => (
                  <ListItemButton key={index} className={classes.listButton}>
                    <Grid container className={classes.listItemRow1}>
                      <Link
                        to={`${PATH_APP.customer.root}/profile/${l?.customerId}`}
                        onClick={() => {
                          setListOpen(false);
                        }}
                      >
                        {!!l.firstName
                          ? `${l.firstName} ${l?.lastName ?? ''}`
                          : l.customerId}
                      </Link>
                      <Typography variant="caption" className={classes.leadAge}>
                        {l.leadAge}
                      </Typography>
                    </Grid>
                    <Grid container className={classes.listItemRow2}>
                      <Grid item xs={9} className={classes.leadReferredBy}>
                        <Grid item xs={12}>
                          <span className={classes.label}>
                            Referred by:&nbsp;
                          </span>
                          <span className={classes.referrerValue}>
                            {l.referredBy || '--'}
                          </span>
                        </Grid>
                        <Grid item xs={12}>
                          <span className={classes.label}>Campaign:&nbsp;</span>
                          <span className={classes.campaignValue}>
                            {l.campaign || '--'}
                          </span>
                        </Grid>
                        {l.referrizerLink && l.origin === 'REFERRIZER' && (
                          <Grid item xs={12}>
                            <Box
                              className={classes.customerName}
                              onClick={() => openReferral(l.referrizerLink)}
                            >
                              <span className={classes.capaignLink}>
                                View in Referrizer
                              </span>
                              <span className={classes.arrowIcon}>
                                <UpRightArrowIcon />
                              </span>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                      <Grid item xs={3}>
                        <Box className={classes.dismiss}>
                          <Button
                            variant="outlined"
                            onClick={() => dismissLead(l.customerId)}
                          >
                            Dismiss
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </ListItemButton>
                ))}
            </List>
            <Grid className={classes.seeAllButton}>
              {leadCount === 0 && (
                <Box className={classes.noItemsMsg}>No leads to show.</Box>
              )}
              {leads?.length > leadItemsToShow && (
                <Box sx={{ margin: 'auto' }}>
                  {leadItemsToShow === 8 ? (
                    <IconButton size="small" onClick={showMoreLeads}>
                      <Chip
                        className={classes.showMoreButton}
                        clickable
                        color="primary"
                        variant="outlined"
                        label={`See All Leads`}
                      />
                    </IconButton>
                  ) : (
                    <IconButton size="small" onClick={showLessLeads}>
                      <Chip
                        className={classes.showMoreButton}
                        clickable
                        variant="outlined"
                        color="primary"
                        label={`See Less Leads`}
                      />
                    </IconButton>
                  )}
                </Box>
              )}
              {leads?.length === leadItemsToShow && (
                <Box className={classes.showingAll}>
                  Showing {leadItemsToShow} of {leads?.length} Leads
                </Box>
              )}
            </Grid>
          </>
        )}
      </Box>
    </ClickAwayListener>
  );

  return (
    <>
      <Tooltip title={isOpen ? '' : 'Activity'} placement="right">
        <ListItem key={'search'} disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            className={navClasses.linkButton}
            onClick={toggleListOpen}
            onMouseEnter={userActionGetNoteCount}
            sx={{
              justifyContent: isOpen ? 'initial' : 'center'
            }}
          >
            <ListItemIcon
              className={navClasses.linkIcon}
              sx={{ mr: isOpen ? 2 : 'auto' }}
            >
              <BellIcon />
            </ListItemIcon>
            <ListItemText
              primary={isOpen ? 'Activity' : ''}
              classes={{ primary: navClasses.linkText }}
              sx={{ opacity: isOpen ? 1 : 0 }}
            />
            {totalCount > 0 && (
              <div
                data-testid="activity-badge"
                className={`${classes.activityBadge} ${
                  !isOpen ? 'condensed' : ''
                }`}
              >
                {totalCount}
              </div>
            )}
          </ListItemButton>
          {listOpen && ReactDOM.createPortal(popout, document.body)}
        </ListItem>
      </Tooltip>
      {showAddEditNoteModal && (
        <AddEditNoteModal
          isOpen={showAddEditNoteModal}
          setIsOpen={open => {
            if (!open) {
              setShowAddEditNoteModal(false);
              setSelectedNote(null);
              getAlertCount();
              getNotes();
            }
          }}
          note={reshapeNoteForEdit(selectedNote)}
          customerId={selectedNote?.customer?.id}
          titleTextOverride={`Editing Note for ${selectedNote?.customer?.fullName}`}
        />
      )}
    </>
  );
};

export default Activity;
